import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from 'recharts'
import {
  Clock, TrendingUp, TrendingDown, DollarSign,
  Activity, ShieldAlert, Layers, Columns3,
} from 'lucide-react'
import { LOAN_STAGES } from '@/types'
import type { LoanStage } from '@/types'
import { useCommandCenterLoans } from '@/hooks/useDemoDataAccess'
import { useLiveTriggerAlerts, useLoansWithLiveTriggers } from '@/hooks/useLiveTriggerAlerts'
import { useGridDensity } from '@/hooks/useGridDensity'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { DealGrid } from '@/pages/pipeline-intelligence/DealGrid'
import { GridColumnPanel } from '@/pages/pipeline-intelligence/GridColumnPanel'
import { RecencySelect } from '@/pages/pipeline-intelligence/RecencySelect'
import { COLUMN_MAP } from '@/pages/pipeline-intelligence/columns'
import {
  DEFAULT_VIEW_SORT,
  OLDEST_FIRST_SORT,
  type ViewSort,
} from '@/pages/pipeline-intelligence/savedViewExport'
import { withinRecency } from '@/pages/pipeline-intelligence/helpers'
import type { ColumnId, RecencyFilter } from '@/pages/pipeline-intelligence/types'
import { DEFAULT_COLUMNS } from '@/pages/pipeline-intelligence/types'
import { layoutColumnIds, screenLayoutFor } from '@/pages/pipeline-intelligence/screenLayout'
import PipelineReconciliation from '@/pages/pipeline-intelligence/PipelineReconciliation'
import ExportIcons from '@/components/shared/ExportIcons'
import { pipelineExportSettingsService } from '@/services/pipelineExportSettings'
import { BACKSHOP_TEMPLATE, DEFAULT_PIPELINE_EXPORT_SETTINGS } from '@/services/pipelineExportDefaults'
import FunnelPanel from '@/components/dashboards/FunnelPanel'
import type { FunnelPanelData } from '@/types/dashboards'
import {
  CHART_AXIS_TICK,
  CHART_LEGEND_STYLE,
  CHART_TOOLTIP_PROPS,
  chartAxisPropsMinimal,
  chartPartOfWholeColor,
  chartPieColor,
} from '@/config/chartTheme'
import {
  STAGE_AXIS_FONT_PX,
  STAGE_AXIS_WIDTH,
  coverageLabel,
  deriveMetrics,
} from './commandCenterMetrics'
import { DEFAULT_CURRENCY, formatMoneyByCurrency, type MoneyByCurrency } from './pipeline-intelligence/currency'

// Kept importable from here: the metrics used to live in this file.
// eslint-disable-next-line react-refresh/only-export-components
export { deriveMetrics }

const STAGES: LoanStage[] = [...LOAN_STAGES]

const fmt = (n: number) => {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`
  return `$${n.toLocaleString()}`
}

const NO_VALUE = '—'

/** The operating system's default double-click interval (Windows: 500 ms). */
const DOUBLE_CLICK_MS = 500

/** A total as a reader sees it: one figure for a one-currency book, each currency's
 *  own subtotal for a mixed one (never added across), a dash for none. */
const fmtMoney = (summary: MoneyByCurrency) => formatMoneyByCurrency(summary, fmt)

/**
 * One stage name on one line. Recharts' own tick wraps a label against the
 * axis width using a width it measures once and caches — measured before the
 * web font loads, the long stage names wrapped onto two or three lines and
 * overprinted each other (CRE-14). The axis is sized for the longest name
 * (STAGE_AXIS_WIDTH), so the label is drawn as plain text, never wrapped.
 */
export function StageAxisTick({ x, y, payload }: { x?: number; y?: number; payload?: { value?: unknown } }) {
  return (
    <text
      x={x}
      y={y}
      dy="0.355em"
      textAnchor="end"
      fontSize={STAGE_AXIS_FONT_PX}
      fill={CHART_AXIS_TICK.fill}
      className="recharts-cartesian-axis-tick-value"
    >
      {String(payload?.value ?? '')}
    </text>
  )
}

/* ── component ─────────────────────────────────────────────────────────── */
export default function CommandCenter() {
  // Command Center is always the live book. GET /loans/ carries no triggers,
  // so each deal gets its /triggers/ rows here: the deal table is "the same
  // table as Pipeline", and its Exceptions column ("Overdue requirement") and
  // the dossier it opens must read the same as they do there.
  const loans = useLoansWithLiveTriggers(useCommandCenterLoans(), true)
  // The same remembered preference as Pipeline Intelligence — saving it and
  // ignoring it on the other grid is exactly the no-reach failure.
  const { density } = useGridDensity()

  // KPIs, charts, alerts and the table all share this loan set.
  const metrics = useMemo(() => deriveMetrics(loans), [loans])
  const { dealCount, totalPipeline, stageVolume, productMix } = metrics
  // The two charts need one scale, so they draw the book's lead currency; a mixed
  // book says which currencies it left out rather than adding them in.
  const chartCaption = metrics.currency.mixed
    ? ` · ${metrics.currency.chart} only — ${metrics.currency.chartExcluded.join(', ')} not plotted`
    : metrics.currency.chart === DEFAULT_CURRENCY
      ? ''
      : ` · ${metrics.currency.chart}`

  // Open triggers come from /triggers/ — `loan.triggers` is never on the /loans
  // payload, which is why the tile and feed always read 0 (EXEC-06).
  const triggerAlerts = useLiveTriggerAlerts(loans)
  const visibleAlerts = triggerAlerts.alerts

  // Same stageVolume the bar chart reads — deal counts in true pipeline
  // order, not the invented linear-decay numbers PortfolioAnalytics.tsx's
  // Conversion Funnel panel uses. No new query: this is a client-side
  // reshape of the real per-stage counts already computed above.
  const funnelData: FunnelPanelData = useMemo(
    () => ({
      stages: stageVolume.map((s, i) => ({
        key: STAGES[i],
        label: s.stage,
        value: s.count,
      })),
    }),
    [stageVolume],
  )

  // The same grid as Pipeline Intelligence, in the same layout: the Backshop pipeline report's
  // columns, headers and stage groups, which the server serves with the export settings (the one
  // definition Print, PDF and Excel render from). Nothing here types a column list of its own.
  const exportSettingsQuery = useQuery({
    queryKey: ['pipeline-export-settings'],
    queryFn: () => pipelineExportSettingsService.get(),
    staleTime: 60_000,
  })
  const exportSettings = exportSettingsQuery.data ?? DEFAULT_PIPELINE_EXPORT_SETTINGS
  const reportLayout = exportSettings.layout !== 'legacy' ? exportSettings.backshopLayout : undefined
  // Until the server has said which layout this is, the grid waits rather than drawing the wrong one.
  const layoutPending = exportSettings.layout !== 'legacy' && !reportLayout && exportSettingsQuery.isLoading
  const [ownColumns, setOwnColumns] = useState<ColumnId[]>(DEFAULT_COLUMNS)
  const columns = reportLayout ? layoutColumnIds(reportLayout) : ownColumns
  const screenLayout = useMemo(() => screenLayoutFor(reportLayout, columns), [reportLayout, columns])
  // R-12: columns added after the table's own (Last modified, Date added), on
  // this screen only, and the deals added or changed recently. The KPIs and
  // charts above still read the whole live book.
  const [extraColumns, setExtraColumns] = useState<ColumnId[]>([])
  const gridColumns = useMemo(
    () => [...columns, ...extraColumns.filter((id) => !columns.includes(id))],
    [columns, extraColumns],
  )
  // Over the report layout only the added columns change; the plain grid's
  // columns are all the desk's own.
  const setGridColumns = (next: ColumnId[]) => {
    if (screenLayout) {
      setExtraColumns(next.filter((id) => !columns.includes(id)))
      return
    }
    setOwnColumns(next)
    setExtraColumns([])
  }
  // The Columns button while its panel is open: the panel opens above it.
  const [columnAnchor, setColumnAnchor] = useState<HTMLElement | null>(null)
  const [recency, setRecency] = useState<RecencyFilter | undefined>(undefined)
  const [stageView, setStageView] = useState<'volume' | 'conversion'>('volume')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // The order the desk chose by clicking a header; until then the grid opens in its own order: over the
  // report layout each stage reads oldest to newest by Days in Status, as Print does.
  const [chosenSort, setChosenSort] = useState<ViewSort | null>(null)
  const sort: ViewSort = chosenSort ?? (screenLayout ? OLDEST_FIRST_SORT : DEFAULT_VIEW_SORT)

  const sortedDeals = useMemo(() => {
    const column = COLUMN_MAP.get(sort.id)
    const rows = loans.filter((loan) => withinRecency(loan, recency))
    rows.sort((a, b) => {
      const left = column?.value(a) ?? ''
      const right = column?.value(b) ?? ''
      if (typeof left === 'number' && typeof right === 'number') {
        return sort.direction === 'asc' ? left - right : right - left
      }
      return sort.direction === 'asc'
        ? String(left).localeCompare(String(right))
        : String(right).localeCompare(String(left))
    })
    return rows
  }, [loans, sort, recency])

  const openDealDossier = (dealId: string) => {
    setSelectedId(dealId)
    const loan = loans.find((row) => row.id === dealId) ?? null
    useDealDossierStore.getState().openDossier(dealId, 'overview', loan)
  }

  // A click on a row opens the deal file here, so a double-click is that click
  // and a second one that lands on the deal file now covering the table. The
  // second click is the same gesture, not a choice made inside the deal file:
  // for one double-click interval it reaches nothing.
  const releaseStrayClick = useRef<(() => void) | null>(null)
  useEffect(() => () => releaseStrayClick.current?.(), [])
  const openFromGrid = (dealId: string) => {
    openDealDossier(dealId)
    releaseStrayClick.current?.()
    const swallow = (event: Event) => {
      event.stopPropagation()
      event.preventDefault()
    }
    window.addEventListener('click', swallow, true)
    window.addEventListener('dblclick', swallow, true)
    const timer = window.setTimeout(() => releaseStrayClick.current?.(), DOUBLE_CLICK_MS)
    releaseStrayClick.current = () => {
      window.clearTimeout(timer)
      window.removeEventListener('click', swallow, true)
      window.removeEventListener('dblclick', swallow, true)
      releaseStrayClick.current = null
    }
  }

  // Every tile states what it stands on: an average its coverage, a total its
  // definition — "YTD Funded $94M" was two loans funded in 2024 and 2025.
  const kpis = [
    {
      label: 'Total Pipeline',
      value: fmtMoney(totalPipeline),
      sub: metrics.currency.mixed ? 'Per currency, never added' : 'All active deals, any stage',
    },
    { label: 'Deal Count', value: String(dealCount), sub: 'Active, not dead or archived' },
    {
      label: 'Wtd Avg DSCR',
      value: metrics.wtdDSCR == null ? NO_VALUE : `${metrics.wtdDSCR.toFixed(2)}x`,
      // A ratio weighted by amount has no honest weight across currencies.
      sub: metrics.currency.mixed && metrics.wtdDSCR == null && metrics.dscrCoverage.measured > 0
        ? 'Not weighted across currencies'
        : coverageLabel('DSCR', metrics.dscrCoverage),
    },
    {
      label: 'Wtd Avg LTV',
      value: metrics.wtdLTV == null ? NO_VALUE : `${metrics.wtdLTV.toFixed(1)}%`,
      sub: metrics.currency.mixed && metrics.wtdLTV == null && metrics.ltvCoverage.measured > 0
        ? 'Not weighted across currencies'
        : coverageLabel('LTV', metrics.ltvCoverage),
    },
    {
      label: 'YTD Funded',
      value: fmtMoney(metrics.ytdFunded),
      sub: `${metrics.ytdFundedCount} funded in ${metrics.year}`,
    },
    {
      label: 'Overdue Triggers',
      // A read that failed, or has not answered, is no count: "0 open" would say the book is clear.
      value: triggerAlerts.available && !triggerAlerts.failed && !triggerAlerts.isLoading
        ? String(triggerAlerts.overdueCount)
        : NO_VALUE,
      sub: !triggerAlerts.available
        ? 'Not visible to your role'
        : triggerAlerts.failed
          ? 'Could not be read'
          : triggerAlerts.isLoading
            ? 'Loading…'
            : `${visibleAlerts.length} open`,
      negative: triggerAlerts.available && !triggerAlerts.failed && triggerAlerts.overdueCount > 0,
    },
    { label: 'In Closing', value: String(metrics.inClosing), sub: 'IC approved or rate locked' },
    {
      label: 'Avg Days in Status',
      value: metrics.avgDaysInStatus == null ? NO_VALUE : String(metrics.avgDaysInStatus),
      sub: `Current status · ${metrics.statusCoverage.measured} of ${metrics.statusCoverage.population}`,
    },
  ]

  const kpiIcons = [DollarSign, Layers, Activity, TrendingDown, TrendingUp, ShieldAlert, Clock, Clock]

  return (
    <div className="flex flex-col h-full px-4 py-3 gap-2 overflow-hidden">
      {/* ── KPI Strip ───────────────────────────────────── */}
      <div className="grid grid-cols-8 gap-2 flex-shrink-0" style={{ height: 66 }}>
        {kpis.map((k, i) => {
          const Icon = kpiIcons[i]
          return (
            <div key={k.label} className="surface-panel flex min-w-0 flex-col items-center justify-center rounded-lg px-3 py-1.5 text-center">
              <div className="flex items-center justify-center gap-1">
                <Icon size={10} className="shrink-0 text-stone-600" />
                {/* Tracking 0.12em: "Avg Days in Status" is the longest label in the strip and spilled past its
                    tile at 1366px with the old 0.18em. */}
                <span className="truncate font-mono text-[9px] uppercase tracking-[0.12em] text-stone-500">{k.label}</span>
              </div>
              <div className="flex max-w-full items-baseline justify-center gap-1.5">
                {/* A mixed-currency total lists each currency's subtotal, so it can run long:
                    it steps down in size and truncates, the full text in the tooltip. */}
                <span
                  className={`max-w-full truncate font-bold leading-tight ${k.value.length > 14 ? 'text-[11px]' : 'text-[18px]'} ${k.negative ? 'text-red-300' : 'text-stone-100'}`}
                  title={k.value}
                >
                  {k.value}
                </span>
              </div>
              <span className="max-w-full truncate text-[9px] leading-tight text-stone-500" title={k.sub}>
                {k.sub}
              </span>
            </div>
          )
        })}
      </div>

      {/* Register P1: Deal Count above is derived from `/loans/`. This states
          what that number is, how much of it intake put there, and what intake
          still holds — the same reconciliation the deal list and Intake show,
          from the same payload. Command Center is always the live book. */}
      <PipelineReconciliation onScreen={dealCount} live />

      {/* ── Main grid ───────────────────────────────────── */}
      <div className="flex-1 grid grid-cols-[1fr_320px] grid-rows-[280px_1fr] gap-2 min-h-0">

        {/* ── Pipeline Waterfall ─────────────────────────── */}
        <div className="surface-panel rounded-lg p-3 flex flex-col">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500">
              {stageView === 'volume' ? `Pipeline Volume by Stage${chartCaption}` : 'Pipeline Conversion by Stage'}
            </span>
            <div className="flex items-center gap-1">
              {(['volume', 'conversion'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setStageView(v)}
                  className={`rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider transition-colors ${
                    stageView === v
                      ? 'bg-amber-400/10 text-amber-300'
                      : 'text-stone-600 hover:text-stone-400'
                  }`}
                >
                  {v === 'volume' ? 'Volume' : 'Conversion'}
                </button>
              ))}
            </div>
          </div>
          <div key={stageView} className="flex-1 min-h-0 animate-chart-reveal">
            {stageView === 'volume' ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stageVolume} layout="vertical" margin={{ left: 10, right: 30, top: 4, bottom: 4 }}>
                  <XAxis type="number" tickFormatter={v => `$${v}M`} {...chartAxisPropsMinimal()} />
                  <YAxis
                    type="category"
                    dataKey="stage"
                    width={STAGE_AXIS_WIDTH}
                    {...chartAxisPropsMinimal(STAGE_AXIS_FONT_PX)}
                    tick={<StageAxisTick />}
                  />
                  <Tooltip
                    {...CHART_TOOLTIP_PROPS}
                    formatter={(v: number) => [`$${v.toFixed(0)}M`, 'Volume']}
                  />
                  <Bar dataKey="volume" radius={[0, 4, 4, 0]} maxBarSize={22}>
                    {stageVolume.map((_, i) => (
                      <Cell key={i} fill={chartPartOfWholeColor(i, stageVolume.length)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full overflow-y-auto pr-1">
                <FunnelPanel data={funnelData} />
              </div>
            )}
          </div>
        </div>

        {/* ── Alert Feed ────────────────────────────────── */}
        <div className="row-span-2 surface-panel rounded-lg p-3 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500">Alert Feed</span>
            <span className="text-[10px] text-amber-300">
              {triggerAlerts.available
                ? `${visibleAlerts.length} open · ${triggerAlerts.overdueCount} overdue`
                : 'Unavailable'}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
            {visibleAlerts.length === 0 && (
              <p className="text-[11px] text-stone-600 font-mono py-2">
                {!triggerAlerts.available
                  ? 'Deal triggers are not visible to your role.'
                  : triggerAlerts.failed
                    ? 'Open triggers could not be read. Nothing is shown until they can.'
                    : triggerAlerts.isLoading
                      ? 'Loading open triggers…'
                      : 'No open triggers on the live book.'}
              </p>
            )}
            {visibleAlerts.map((a, i) => {
              const borderColor = a.severity === 'high' ? 'border-l-red-400' : a.severity === 'medium' ? 'border-l-amber-400' : 'border-l-emerald-400'
              return (
                <div
                  key={`${a.deal}-${i}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDealDossier(a.deal)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      openDealDossier(a.deal)
                    }
                  }}
                  className={`border-l-2 ${borderColor} bg-white/[0.02] rounded-r px-2.5 py-2 cursor-pointer hover:bg-white/[0.04] transition-colors`}
                >
                  <p className="text-[12px] text-stone-200 leading-tight">{a.text}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-stone-500">{a.deal}</span>
                    <span className={`text-[10px] ${a.overdue ? 'text-red-300' : 'text-stone-600'}`}>
                      {a.overdue ? `Overdue · due ${a.due}` : `Due ${a.due}`}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* ── Product Mix (below alerts) ──────────────── */}
          <div className="mt-2 pt-2 border-t border-amber-400/10 flex-shrink-0">
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500">
              Product Mix · stated position{chartCaption}
            </span>
            <div style={{ height: 190 }}>
              {productMix.length === 0 ? (
                <p className="py-8 text-center font-mono text-[11px] text-stone-600">No product mix for this filter.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={productMix}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      strokeWidth={1}
                      stroke="#111111"
                    >
                      {productMix.map((_, i) => (
                        <Cell key={i} fill={chartPieColor(i)} />
                      ))}
                    </Pie>
                    <Tooltip
                      {...CHART_TOOLTIP_PROPS}
                      formatter={(v: number) => [`$${v.toFixed(0)}M`, 'Volume']}
                    />
                    <Legend iconSize={8} wrapperStyle={CHART_LEGEND_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* ── Pipeline Deals (same DealGrid as Pipeline page) ─ */}
        <div className="surface-panel rounded-lg p-3 flex flex-col overflow-hidden">
          <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500">
              Pipeline Deals
            </span>
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[9px] text-stone-600">
                {recency
                  ? `${sortedDeals.length} of ${loans.length} deals · click to open dossier`
                  : 'Same table as Pipeline · click to open dossier'}
              </span>
              <RecencySelect
                value={recency}
                onChange={setRecency}
                className="shrink-0 rounded border border-stone-700 bg-black/40 px-1.5 py-0.5 text-[10px] text-stone-200 [color-scheme:dark]"
              />
              <button
                type="button"
                aria-expanded={columnAnchor != null}
                onClick={(event) => {
                  const button = event.currentTarget
                  setColumnAnchor((open) => (open ? null : button))
                }}
                className="inline-flex shrink-0 items-center gap-1 rounded border border-stone-700 px-2 py-0.5 text-[10px] text-stone-300"
              >
                <Columns3 className="h-3 w-3" /> Columns
              </button>
              {columnAnchor ? (
                <GridColumnPanel
                  visible={gridColumns}
                  fixed={screenLayout ? columns : undefined}
                  setVisible={setGridColumns}
                  anchor={columnAnchor}
                  onClose={() => setColumnAnchor(null)}
                />
              ) : null}
              {/* The same Print / PDF / Excel as the Pipeline page: one server template, so what
                  prints here is what the Pipeline page prints and what this grid shows. */}
              {exportSettings.layout !== 'legacy' ? (
                <ExportIcons
                  template={BACKSHOP_TEMPLATE}
                  formats={['pdf', 'xlsx']}
                  // The table's recency choice, applied by `builder.within_recency`, so the file
                  // carries the deals the table shows.
                  filters={recency ? { recency } : {}}
                  filenameBase="Command_Center_Pipeline_Report"
                  printTitle="Print the Pipeline Report (Backshop layout)"
                />
              ) : null}
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            {layoutPending ? (
              <div className="flex h-40 items-center justify-center text-sm text-stone-500">
                Loading the pipeline report layout…
              </div>
            ) : (
              <DealGrid
                editableResponsibleParty
                density={density}
                layout={screenLayout}
                loans={sortedDeals}
                columns={gridColumns}
                selectedId={selectedId}
                onSelect={(loan) => openFromGrid(loan.id)}
                onOpen={(loan) => openDealDossier(loan.id)}
                sort={sort}
                onSort={(id) =>
                  setChosenSort(
                    sort.id === id
                      ? { id, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
                      : { id, direction: 'desc' },
                  )
                }
                // The report's columns are fixed, as on the Pipeline page.
                onReorder={screenLayout ? undefined : setGridColumns}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex h-8 flex-shrink-0 items-center rounded-lg border border-amber-400/10 bg-black/30 px-3">
        <span className="font-mono text-[10px] text-stone-500">
          Live pipeline · {dealCount} deal{dealCount === 1 ? '' : 's'} · {fmtMoney(totalPipeline)} total
          {metrics.illustrativeDeals > 0 &&
            ` · includes ${metrics.illustrativeDeals} illustrative seed deal${metrics.illustrativeDeals === 1 ? '' : 's'} (${fmtMoney(metrics.illustrativeAmount)}) — not client data`}
        </span>
      </div>
    </div>
  )
}
